<script lang="ts">
import EnvBanner from '@/components/EnvBanner.vue'
import ImpersonateBanner from '@/components/ImpersonateBanner.vue'
import AppBottomNav from '@/components/common/AppBottomNav.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import AppHeader from '@/components/common/AppHeader.vue'
import AppMagicLinkOverlay from '@/components/common/AppMagicLinkOverlay.vue'
import AppSidebarNav from '@/components/common/AppSidebarNav.vue'
import AppAlert from '@/components/modals/AppAlert.vue'
import StagingEnv from '@/components/modals/StagingEnv.vue'
import AppNotification from '@/components/notifications/AppNotification.vue'
import { useCampaignsStore } from '@/stores/campaigns'
import { useInstantWinsStore } from '@/stores/instant-wins'
import { usePageProgressStore } from '@/stores/page-progress'
import { usePrizesStore } from '@/stores/prizes'
import type { Campaign, Nullable, PartnershipCampaign } from '@/types'
import { isPartnershipCampaign } from '@/utils/type-guard-utils'
import { useCookiebot } from '@ambitiondev/vue-cookiebot'
import { usePreferredReducedMotion } from '@vueuse/core'
import { defineComponent, toValue, watch } from 'vue'

import { useAuthWatchers } from './composables/useAuthWatchers'
import { useRouteMeta } from './plugins/useRouteMeta'

// @see https://www.w3.org/WAI/ARIA/apg/practices/landmark-regions/

// @see https://vercel.com/docs/analytics
// @see https://vercel.com/docs/speed-insights

export default defineComponent({
  components: {
    AppAlert,
    AppBottomNav,
    AppFooter,
    AppHeader,
    AppMagicLinkOverlay,
    AppNotification,
    AppSidebarNav,
    EnvBanner,
    ImpersonateBanner,
    StagingEnv,
  },
  setup() {
    const { consentBanner } = useCookiebot()
    const routeMeta = useRouteMeta()

    return {
      prefersReduceMotion: usePreferredReducedMotion(),
      consentBanner,
      routeMeta,
    }
  },
  data() {
    return {
      show: false,
    }
  },
  computed: {
    campaignStyles(): Nullable<{
      header?: string
      footer?: string
      main?: string
    }> {
      const campaign = (
        this.$router.currentRoute.value.meta.props as {
          campaign?: Campaign | PartnershipCampaign
        }
      )?.campaign

      if (isPartnershipCampaign(campaign)) {
        if (campaign.partner_name.toLowerCase() === 'casino.org') {
          return {
            header: `background: ${toValue(campaign.header_background_color)};`,
          }
        }
        if (campaign.partner_name.toLowerCase() === 'vodafone') {
          return {
            header: `border-bottom: 0px;`,
            footer: 'border-top 0px;',
          }
        }

        return {
          header: `
            background: ${toValue(campaign.header_background_color ?? campaign.brand_secondary_color)}; 
            border-bottom: 20px solid ${toValue(campaign.brand_color)};
          `,
          footer: `
            background: ${toValue(campaign.brand_secondary_color)}; 
            border-top: 20px solid ${toValue(campaign.brand_color)};
          `,
          main: `
            background: ${toValue(campaign.brand_secondary_color)}; 
          `,
        }
      }

      return null
    },
    headerRouteOptions() {
      return this.$router.currentRoute.value.meta.nav?.header
    },
    hideNav(): boolean {
      return (
        this.$router.currentRoute.value.name === 'campaign' ||
        this.$router.currentRoute.value.name === 'referral-box' ||
        this.$router.currentRoute.value.name === 'welcome' ||
        !!this.headerRouteOptions?.logoImage
      )
    },
    hideBottomNav(): boolean {
      return this.routeMeta.isGamePlayRoute(
        this.$router.currentRoute.value.name as string
      )
    },
  },
  async mounted() {
    const pageProgress = usePageProgressStore()

    if (!this.prefersReduceMotion) {
      this.show = true
    } else {
      watch(
        () => pageProgress.isLoading,
        (isLoading) => {
          this.show = false
          setTimeout(() => {
            this.show = !isLoading
          }, 200)
        },
        {
          immediate: true,
        }
      )
    }

    await this.consentBanner()

    // Set up authentication state watchers for rewards subscriptions
    useAuthWatchers()
      .setupAuthWatchers()
      .catch((error) => {
        console.error('Failed to setup auth watchers:', error)
      })

    // Subscribe to real-time content updates
    const instantWinsStore = useInstantWinsStore()
    const prizesStore = usePrizesStore()
    const campaignsStore = useCampaignsStore()

    instantWinsStore.subscribeToUpdates()
    prizesStore.subscribeToUpdates()
    campaignsStore.subscribeToUpdates()
  },
  beforeUnmount() {
    // Clean up subscriptions when app unmounts
    const instantWinsStore = useInstantWinsStore()
    const prizesStore = usePrizesStore()
    const campaignsStore = useCampaignsStore()

    instantWinsStore.unsubscribeFromUpdates()
    prizesStore.unsubscribeFromUpdates()
    campaignsStore.unsubscribeFromUpdates()
  },
})
</script>

<template>
  <EnvBanner v-if="APP_ENV !== 'production'" />
  <ImpersonateBanner v-if="$userStore.isImpersonating" />

  <!-- <AppSkipLink content-id="member-zone" /> -->
  <StagingEnv />

  <AppSidebarNav v-if="!hideNav" :class="`${$theme.type} ${$theme.name}`" />

  <div id="main-container">
    <AppHeader
      :style="campaignStyles?.header"
      :class="`${$theme.type} ${$theme.name}`"
    />

    <RouterView v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <main
          v-if="show"
          class="flex w-full min-w-0 flex-col"
          style="min-height: calc(100vh - 65px)"
          :class="`${String($router.currentRoute.value.name)}-page ${$theme.type} ${$theme.name}`"
          :style="campaignStyles?.main"
        >
          <component :is="Component" />
          <div id="after-content-teleport"></div>
        </main>
      </transition>
    </RouterView>

    <AppFooter
      v-once
      :style="campaignStyles?.footer"
      :class="`${$theme.type} ${$theme.name}`"
    />
  </div>

  <AppBottomNav v-if="!hideNav && !hideBottomNav" />

  <AppAlert />
  <AppNotification />
  <AppMagicLinkOverlay />
</template>

<style lang="scss" scoped>
@media (prefers-reduced-motion) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}

// main will-change

.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
